import { createMemo, createSignal, onMount, For, Show } from "solid-js";
import { useApi, isOk } from "../api";
import { useToast } from "../components/MessageBox";
import { useUserContext } from "../components/UserContextProvider";
import { EmptyState } from "../components/EmptyState";
import { Skeleton } from "../components/Skeleton";
import Text from "../components/Text";
import styles from "./Downloads.module.css";
import { useRoleParser } from "../hooks/permissions";
import { useTranslation } from "../hooks/useTranslation";

function compareVersionsDesc(a: string, b: string) {
    const left = a.split("-")[0].split(".").map(Number);
    const right = b.split("-")[0].split(".").map(Number);

    for (let i = 0; i < 3; i++) {
        const delta = (right[i] || 0) - (left[i] || 0);
        if (delta !== 0) return delta;
    }
    return 0;
}

export function DownloadsPage() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { parseRoles, roles } = useRoleParser();
    const { user } = useUserContext();

    const [loading, setLoading] = createSignal(true);
    const [showUnsupportedVersions, setShowUnsupportedVersions] = createSignal(false);
    const [downloads, setDownloads] = createSignal<string[]>([]);
    const [descriptions, setDescriptions] = createSignal<Record<string, string>>({});
    const [selectedVersion, setSelectedVersion] = createSignal<string | null>(null);
    const [description, setDescription] = createSignal("");
    const [saving, setSaving] = createSignal(false);

    const isAdmin = () => {
        let flags = parseRoles(user().permissions ?? 0, roles);
        return flags[2] && user().showAdminUiInMemberApp === true; // the Admin flag is at index 2
    }

    onMount(async () => {
        const [versionsRes, changelogRes] = await Promise.all([
            api.jarDownload.getVersions(),
            api.changelog.getVersions(),
        ]);

        if (Array.isArray(versionsRes.data)) {
            setDownloads([...versionsRes.data].sort(compareVersionsDesc));
        } else {
            toast?.error(t("DOWNLOADS_LOAD_ERROR"));
        }

        if (isOk(changelogRes.data)) {
            setDescriptions({ ...changelogRes.data.value.versions });
        }

        setLoading(false);
    });

    const filteredDownloads = createMemo(() => {
        if (showUnsupportedVersions()) return downloads();
        return downloads().filter(version => !!descriptions()[version]);
    });

    async function saveDescription() {
        const version = selectedVersion();
        if (!version) return;

        setSaving(true);
        const res = await api.changelog.postChangelog({ version, description: description() });
        setSaving(false);

        if (isOk(res.data)) {
            setDescriptions({ ...descriptions(), [version]: description() });
            toast?.info(t("DOWNLOADS_DESC_SAVED", version));
        } else {
            toast?.error(t("DOWNLOADS_DESC_SAVE_ERROR", version));
        }
    }

    return (
        <div class={styles.downloadsPage}>
            <h1 class={styles.mainTitle}><Text val="ADDON_JAR_DOWNLOAD" /></h1>
            <div class={styles.contentContainer}>
                <div class={styles.downloads}>
                    <h1 class={styles.versionsTitle}><Text val="VERSIONS" /></h1>
                    <label class="h hcenter">
                        <span><Text val="DOWNLOADS_SHOW_UNSUPPORTED" /></span>
                        <input
                            type="checkbox"
                            checked={showUnsupportedVersions()}
                            onChange={(e) => setShowUnsupportedVersions(e.currentTarget.checked)}
                        />
                    </label>
                    <Show when={!loading()} fallback={<Skeleton variant="rect" height="3.25rem" radius="var(--radius-md)" count={6} />}>
                        <For each={filteredDownloads()} fallback={
                            <EmptyState
                                illustration="generic"
                                title={t("DOWNLOADS_EMPTY_TITLE")}
                                hint={t("DOWNLOADS_EMPTY_HINT")}
                            />
                        }>
                            {version => (
                                <div onclick={() => {
                                        setSelectedVersion(version);
                                        setDescription(descriptions()[version] ?? "");
                                    }} class={styles.download + (descriptions()[version] ? ` ${styles.downloadActive}` : "")}>
                                    <div style={{ display: "flex", "align-items": "center", gap: "1em" }}>
                                        <a class={styles.versionText}>
                                            {version} {descriptions()[version] ? `ℹ️` : ""}
                                        </a>
                                    </div>
                                </div>
                            )}
                        </For>
                    </Show>
                </div>
                <Show when={selectedVersion()}>
                    {version => (
                        <div class={styles.downloadDetails}>
                            <div>
                                <h1 class={styles.detailsTitle}>{t("DOWNLOADS_DETAILS_FOR", version())}</h1>
                                <Show when={isAdmin()}>
                                    <div>
                                        <textarea id="description"
                                            class={styles.textarea}
                                            value={description()}
                                            onInput={(e) => setDescription(e.currentTarget.value)}
                                            rows="10"
                                            cols="50"
                                            placeholder={t("DOWNLOADS_DESC_PLACEHOLDER")}
                                        ></textarea>
                                        <button class={styles.saveButton} disabled={saving()} onClick={() => saveDescription()}>
                                            <Show when={saving()}><i class="fa-solid fa-spinner fa-spin" /> </Show>
                                            <Text val="SAVE_DESCRIPTION" />
                                        </button>
                                    </div>
                                </Show>
                                <Show when={description() && !isAdmin()}><div class={styles.descriptionText}>{description()}</div></Show>
                            </div>
                            <a target="_blank" class={styles.downloadLink} href={`/api/JarDownload/${version()}`}>
                                <Text val="GOGGLES_DOWNLOAD" />
                            </a>
                        </div>
                    )}
                </Show>
            </div>
        </div>
    );
}
